import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowLeft, Shield, Smartphone, UserPlus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { TurnstileWidget, withTurnstile } from "../components/TurnstileWidget";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [needsSetup, setNeedsSetup] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [setupName, setSetupName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login"); // login | 2fa | 2fa-setup | forgot | forgot-sent | force-change
  const [tempToken, setTempToken] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [qrData, setQrData] = useState(null);
  const [forgotEmail, setForgotEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken("");
    setTurnstileResetKey(k => k + 1);
  }, []);
  // Honeypot field for bot protection
  const [honeypot, setHoneypot] = useState("");

  // Check if fresh install needs first admin setup
  useEffect(() => {
    axios.get(`${API}/setup-status`).then(res => {
      if (res.data.needs_setup) setNeedsSetup(true);
    }).catch(() => {});
  }, []);

  const handleSetup = async (e) => {
    e.preventDefault();
    if (!setupName || !email || !password) { toast.error("All fields required"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, { name: setupName, email, password });
      sessionStorage.setItem("auth_token", res.data.access_token);
      sessionStorage.setItem("auth_user", JSON.stringify(res.data.user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
      toast.success("Admin account created! Welcome.");
      setNeedsSetup(false);
      navigate("/");
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Setup failed");
    } finally { setLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (honeypot) return; // Bot detected
    if (!turnstileToken) { toast.error("Please complete the CAPTCHA challenge"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, withTurnstile(turnstileToken));
      const data = res.data;

      if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setView("2fa");
        resetTurnstile();
        setLoading(false);
        return;
      }

      if (data.must_change_password) {
        setTempToken(data.access_token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
        setView("force-change");
        resetTurnstile();
        setLoading(false);
        return;
      }

      if (data.needs_2fa_setup) {
        sessionStorage.setItem("auth_token", data.access_token);
        sessionStorage.setItem("auth_user", JSON.stringify(data.user));
        axios.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
        const qrRes = await axios.post(`${API}/auth/2fa/setup`, {}, withTurnstile(turnstileToken));
        setQrData(qrRes.data);
        setView("2fa-setup");
        resetTurnstile();
        setLoading(false);
        return;
      }

      // Normal login
      sessionStorage.setItem("auth_token", data.access_token);
      sessionStorage.setItem("auth_user", JSON.stringify(data.user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
      toast.success("Welcome back!");
      navigate("/");
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
      resetTurnstile();
    } finally { setLoading(false); }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (!turnstileToken) { toast.error("Please complete the CAPTCHA challenge"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/validate`, { temp_token: tempToken, code: totpCode }, withTurnstile(turnstileToken));
      sessionStorage.setItem("auth_token", res.data.access_token);
      sessionStorage.setItem("auth_user", JSON.stringify(res.data.user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
      toast.success("Welcome back!");
      navigate("/");
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
      setTotpCode("");
      resetTurnstile();
    } finally { setLoading(false); }
  };

  const handle2FASetup = async (e) => {
    e.preventDefault();
    if (!turnstileToken) { toast.error("Please complete the CAPTCHA challenge"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/verify`, { code: totpCode }, withTurnstile(turnstileToken));
      toast.success("2FA enabled! You're all set.");
      navigate("/");
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code. Try again.");
      setTotpCode("");
      resetTurnstile();
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!turnstileToken) { toast.error("Please complete the CAPTCHA challenge"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail }, withTurnstile(turnstileToken));
      toast.success(res.data.message);
      setView("forgot-sent");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to send reset link"); resetTurnstile(); }
    finally { setLoading(false); }
  };

  // handleResetPassword removed — reset is now handled on the /reset-password page via email link

  const handleForceChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error("Passwords don't match"); return; }
    if (!turnstileToken) { toast.error("Please complete the CAPTCHA challenge"); return; }
    setLoading(true);
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${tempToken}`;
      await axios.post(`${API}/auth/force-change-password`, { new_password: newPassword }, withTurnstile(turnstileToken));
      toast.success("Password changed! Setting up 2FA...");
      
      // Auto-login with new password — need fresh turnstile token
      resetTurnstile();
      // Redirect to login — user must complete full login flow (with CAPTCHA) again
      toast.success("Password changed successfully. Please log in with your new password.");
      setView("login");
      setPassword("");
      setNewPassword(""); setConfirmPassword("");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); resetTurnstile(); }
    finally { setLoading(false); }
  };

  const pwRequirements = newPassword ? [
    { check: newPassword.length >= 10, label: "Min 10 characters" },
    { check: /[A-Z]/.test(newPassword), label: "Uppercase letter" },
    { check: /[a-z]/.test(newPassword), label: "Lowercase letter" },
    { check: /[0-9]/.test(newPassword), label: "Number" },
    { check: /[!@#$%^&*()_+\-=\[\]{}|;':",./<>?]/.test(newPassword), label: "Special character" },
  ] : [];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center justify-center mb-6 bg-card rounded-xl shadow-sm py-5 border border-border">
          <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center overflow-hidden mb-2">
            <img src="/api/logo" alt="SC" className="w-full h-full object-contain" onError={(e) => { e.target.onerror=null; e.target.src="data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 40 40%27%3E%3Crect width=%2740%27 height=%2740%27 rx=%278%27 fill=%27%233B82F6%27/%3E%3Ctext x=%2720%27 y=%2726%27 text-anchor=%27middle%27 fill=%27white%27 font-size=%2716%27 font-weight=%27bold%27%3ESC%3C/text%3E%3C/svg%3E"; }} />
          </div>
          <h1 className="text-xl font-bold text-foreground tracking-tight">SpillConsult Pulse</h1>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Secured Business Intelligence</p>
        </div>

        <Card>
          {/* FIRST ADMIN SETUP (fresh install only) */}
          {needsSetup && view === "login" && (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10"><UserPlus className="w-5 h-5 text-primary" /></div>
                  <div><CardTitle className="text-lg">Initial Setup</CardTitle><CardDescription>Create the first administrator account</CardDescription></div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSetup} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Full Name</Label>
                    <Input placeholder="Your full name" value={setupName} onChange={(e) => setSetupName(e.target.value)} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Email</Label>
                    <Input type="email" placeholder="admin@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Password (min 8, uppercase, lowercase, number, special char)</Label>
                    <Input type="password" placeholder="Strong password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>{loading ? "Creating..." : "Create Admin Account"}</Button>
                </form>
              </CardContent>
            </>
          )}

          {/* LOGIN */}
          {!needsSetup && view === "login" && (
            <>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Sign In</CardTitle>
                <CardDescription>Enter your credentials</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10" required data-testid="login-email" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input type={showPassword ? "text" : "password"} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 pr-10" required data-testid="login-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {/* Honeypot - invisible to humans, bots fill it */}
                  <input type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} style={{ position: "absolute", left: "-9999px", opacity: 0 }} tabIndex={-1} autoComplete="off" />
                  <TurnstileWidget onToken={setTurnstileToken} onExpired={() => setTurnstileToken("")} resetKey={turnstileResetKey} />
                  <Button type="submit" className="w-full" disabled={loading || !turnstileToken} data-testid="login-btn">
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                  <button type="button" onClick={() => { setView("forgot"); resetTurnstile(); }} className="w-full text-center text-xs text-primary hover:underline">Forgot Password?</button>
                </form>
              </CardContent>
            </>
          )}

          {/* 2FA CODE ENTRY */}
          {view === "2fa" && (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10"><Shield className="w-5 h-5 text-primary" /></div>
                  <div><CardTitle className="text-lg">Two-Factor Authentication</CardTitle><CardDescription>Enter the 6-digit code from your authenticator app</CardDescription></div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handle2FAVerify} className="space-y-4">
                  <Input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} placeholder="000000" value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    className="text-center text-2xl tracking-[0.5em] font-mono h-14" autoFocus required />
                  <TurnstileWidget onToken={setTurnstileToken} onExpired={() => setTurnstileToken("")} resetKey={turnstileResetKey} />
                  <Button type="submit" className="w-full" disabled={loading || totpCode.length !== 6 || !turnstileToken}>{loading ? "Verifying..." : "Verify"}</Button>
                  <button type="button" onClick={() => { setView("login"); setTotpCode(""); resetTurnstile(); }} className="w-full text-center text-xs text-muted-foreground hover:underline">Back to login</button>
                </form>
              </CardContent>
            </>
          )}

          {/* 2FA SETUP (mandatory first time) */}
          {view === "2fa-setup" && (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10"><Smartphone className="w-5 h-5 text-amber-500" /></div>
                  <div><CardTitle className="text-lg">Setup Two-Factor Authentication</CardTitle><CardDescription>2FA is mandatory. Scan the QR code with your authenticator app.</CardDescription></div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handle2FASetup} className="space-y-4">
                  {qrData && (
                    <div className="flex flex-col items-center gap-3">
                      <img src={qrData.qr_code} alt="QR Code" className="w-48 h-48 rounded-lg border border-border" />
                      <p className="text-xs text-muted-foreground text-center">Can't scan? Enter this key manually:</p>
                      <code className="text-xs bg-muted px-3 py-1.5 rounded font-mono select-all">{qrData.secret}</code>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-xs">Enter the 6-digit code from your app</Label>
                    <Input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} placeholder="000000" value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                      className="text-center text-2xl tracking-[0.5em] font-mono h-14" required />
                  </div>
                  <TurnstileWidget onToken={setTurnstileToken} onExpired={() => setTurnstileToken("")} resetKey={turnstileResetKey} />
                  <Button type="submit" className="w-full" disabled={loading || totpCode.length !== 6 || !turnstileToken}>{loading ? "Verifying..." : "Activate 2FA"}</Button>
                </form>
              </CardContent>
            </>
          )}

          {/* FORGOT PASSWORD */}
          {view === "forgot" && (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <button onClick={() => setView("login")} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /></button>
                  <div><CardTitle className="text-lg">Forgot Password</CardTitle><CardDescription>Enter your email to receive a reset link</CardDescription></div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleForgotPassword} className="space-y-3">
                  <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" placeholder="you@company.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required /></div>
                  <TurnstileWidget onToken={setTurnstileToken} onExpired={() => setTurnstileToken("")} resetKey={turnstileResetKey} />
                  <Button type="submit" className="w-full" disabled={loading || !turnstileToken}>{loading ? "Sending..." : "Send Reset Link"}</Button>
                </form>
              </CardContent>
            </>
          )}

          {/* FORGOT PASSWORD - EMAIL SENT CONFIRMATION */}
          {view === "forgot-sent" && (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  <div><CardTitle className="text-lg">Check Your Email</CardTitle></div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 text-center space-y-2">
                  <p className="text-sm text-foreground">If an account exists for <strong className="text-primary">{forgotEmail}</strong>, we've sent a password reset link.</p>
                  <p className="text-xs text-muted-foreground">The link expires in 15 minutes and can only be used once.</p>
                </div>
                <div className="space-y-2 text-xs text-muted-foreground">
                  <p>Didn't receive the email?</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Check your spam/junk folder</li>
                    <li>Make sure you entered the correct email</li>
                    <li>Contact your administrator if the issue persists</li>
                  </ul>
                </div>
                <Button variant="outline" className="w-full" onClick={() => { setView("login"); setForgotEmail(""); }}>Back to Login</Button>
              </CardContent>
            </>
          )}

          {/* FORCE CHANGE PASSWORD */}
          {view === "force-change" && (
            <>
              <CardHeader className="pb-3"><CardTitle className="text-lg">Change Password Required</CardTitle><CardDescription>Set a strong password to continue</CardDescription></CardHeader>
              <CardContent>
                <form onSubmit={handleForceChange} className="space-y-3">
                  <div className="space-y-1.5"><Label className="text-xs">New Password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={10} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Confirm</Label><Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required /></div>
                  {pwRequirements.length > 0 && (<div className="grid grid-cols-2 gap-1">{pwRequirements.map((r, i) => (<span key={i} className={`text-[10px] ${r.check ? "text-emerald-600" : "text-muted-foreground"}`}>{r.check ? "✓" : "✗"} {r.label}</span>))}</div>)}
                  <TurnstileWidget onToken={setTurnstileToken} onExpired={() => setTurnstileToken("")} resetKey={turnstileResetKey} />
                  <Button type="submit" className="w-full" disabled={loading || !turnstileToken}>{loading ? "Setting..." : "Set Password & Continue"}</Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>

        <p className="text-center text-[10px] text-muted-foreground mt-4">Protected by brute-force detection, 2FA, and encrypted sessions</p>
      </div>
    </div>
  );
}
